// FileUploader.tsx
import React, { useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { Paper, Typography, Box } from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { parseFile } from '../../../../../../utils/priceListUpload/parseFile';
import type { ParsedFileResult } from '../../../../../../utils/priceListUpload/types';

interface Props {
  onParsed: (parsed: ParsedFileResult) => void;
  onError?: (err: Error | string) => void;
}

const FileUploader: React.FC<Props> = ({ onParsed, onError }) => {
  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    const file = acceptedFiles[0];
    if (!file) return;
    try {
      const parsed = await parseFile(file);
      onParsed(parsed);
    } catch (err: any) {
      const msg = err?.message || String(err);
      if (onError) onError(msg);
      else console.error('Parse error', msg);
    }
  }, [onParsed, onError]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'application/vnd.ms-excel': ['.xls'],
      'text/csv': ['.csv'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
      'application/pdf': ['.pdf']
    },
    maxFiles: 1,
    maxSize: 10 * 1024 * 1024,
  });

  return (
    <Paper 
      {...getRootProps()} 
      sx={{ 
        p: 2, 
        textAlign: 'center', 
        cursor: 'pointer',
        border: 2,
        borderStyle: 'dashed',
        borderColor: isDragActive ? 'primary.main' : 'divider',
        backgroundColor: isDragActive ? 'action.hover' : 'background.paper',
        transition: 'all 0.2s ease-in-out',
        '&:hover': {
          borderColor: 'primary.main',
          backgroundColor: 'action.hover'
        }
      }}
    >
      <input {...getInputProps()} />
      <CloudUploadIcon sx={{ fontSize: 64, color: 'primary.main', mb: 2, opacity: 0.7 }} />
      <Typography variant="h5" gutterBottom fontWeight="medium">
        {isDragActive ? 'Drop file here' : 'Upload Excel '}
      </Typography>
      <Typography variant="body1" color="text.secondary" gutterBottom>
        Drag & drop your file here or click to browse
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
        Supported formats: .xlsx, .xls (Max 10MB)
      </Typography>
      <Box sx={{ mt: 3, p: 2, backgroundColor: 'grey.50', borderRadius: 1 }}>
        <Typography variant="caption" color="text.secondary">
          <strong>Expected format for Excel :</strong><br />
          • Row 1: Vendor name (A1 cell)<br />
          • Row 2: Column headers<br />
          • Row 3+: Data rows<br />
          {/* • <strong>For PDF:</strong> Best results with text-based PDFs containing tabular data */}
        </Typography>
      </Box>
    </Paper>
  );
};

export default FileUploader;